<template>
  <div class="gf_spacing">
    <perfect-scrollbar class="gf_spacing__scroll-body">
      <div class="body">
        <div class="d-flex align-items-center gf_spacing_pack-name">
          <Teleport v-if="isOpenTooltip" to="#tooltips">
            <Tooltip :pointer="true" t-class="text-center" :width="tWidth" :delay="100" />
          </Teleport>
          <span class="gf_spacing_pack-name--value">Spacing Value</span>
          <i
            v-popover:tooltip.top="$t('sidebarGS.styleDetail.spacing.tooltip')"
            class="icon icon-question-circle gf_spacing_pack-name--notice"
            @mouseover="setWidthToolTip"
            @mouseleave="resetWidthToolTip" />
        </div>
        <div class="gf_spacing_pack-elements">
          <template v-for="(element, index) in currentPresetOptions">
            <SpacingItem
              v-if="isDestroyed(element)"
              :key="index"
              :index="index"
              :element="element"
              :enable-edit="enableEdit"
              :enable-duplicate="newElementValid"
              :is-ready-to-save="isReadyToSave"
              @isValid="isValid"
              @onDelete="deleteElement"
              @onDuplicate="duplicateItem" />
          </template>
          <div
            v-if="newElementValid"
            class="d-flex justify-content-center align-items-center gf_spacing_pack-actions"
            @click="addNewElementAction">
            <svg
              class="gf_spacing_pack-actions--icon"
              width="11"
              height="12"
              viewBox="0 0 11 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6 1C6 0.723858 5.77614 0.5 5.5 0.5C5.22386 0.5 5 0.723858 5 1V5.5H0.5C0.223858 5.5 0 5.72386 0 6C0 6.27614 0.223858 6.5 0.5 6.5H5V11C5 11.2761 5.22386 11.5 5.5 11.5C5.77614 11.5 6 11.2761 6 11V6.5H10.5C10.7761 6.5 11 6.27614 11 6C11 5.72386 10.7761 5.5 10.5 5.5H6V1Z"
                fill="#E2E2E2" />
            </svg>
            <span class="gf_spacing_pack-actions--label">Add New Spacing Value</span>
          </div>
        </div>
      </div>
      <GuideCustomizeStyle
        v-if="!obGlobalStyle.tooltipSpacingStyle"
        title="Setup Spacing Value"
        guide="tooltipSpacingStyle"
        link="https://help.gempages.net/articles/customize-global-style#table-content-4"
        content="You can use spacing value to setup margin and padding of elements to create the consistant space rhythm."
        :style-line="{ bottom: '91px', paddingLeft: '0px', paddingRight: '0px' }" />
    </perfect-scrollbar>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import type { PresetOptionSpacing } from '@/types/global-style';
import { presetOptionTypes } from '../../constants/styleConstant';
import { mutations } from '@modules/editor-sidebar';
import SpacingItem from './/SpacingItem.vue';
import { cloneDeep } from '@/utils/common';
import FooterPresetOption from '../../utils/mixins/FooterPresetOption';
import { SPACING_DEFAULT_NAMES } from '@/constants/global-style';
import GuideCustomizeStyle from '../guide/GuideCustomizeStyle.vue';
import HELPER from '@/app/utils/HELPER';

export default defineComponent({
  name: 'Spacing',
  components: {
    SpacingItem,
    GuideCustomizeStyle,
  },
  mixins: [FooterPresetOption],
  data() {
    return {
      enableEdit: [],
      isReadyToSave: true,
      tWidth: 'auto',
      isOpenTooltip: false,
    };
  },
  computed: {
    ...mapState('sidebar', {
      currentPreset: (state) => (state as any).currentPreset,
    }),
    ...mapGetters('sidebar', {
      currentPresetOptions: 'getCurrentPresetOptions',
    }),
    ...mapState('app', {
      shopMeta: 'shopMeta',
    }),
    obGlobalStyle() {
      return this.shopMeta.onboardingGlobalStyle;
    },
    elementNames() {
      return this.currentPresetOptions
        .filter((element: { destroy: any }) => !element.destroy)
        .map((element: { name: any }) => element.name.toUpperCase());
    },
    newElementValid() {
      return this.elementNames.length < 10;
    },
  } as any,
  watch: {
    isReadyToSave(newVal) {
      this.$emit('isReadyToSave', newVal);
    },
  },
  created() {
    this.setPresetTypeId(presetOptionTypes.spacing);
    this.makeCurrentPresetOptions();
  },
  methods: {
    ...mapMutations('sidebar', {
      setPresetTypeId: mutations.SET_PRESET_TYPE_ID,
      setCurrentPresetOptions: mutations.SET_CURRENT_PRESET_OPTIONS,
    }),
    ...mapActions('app', {
      updateShopMeta: 'updateShopMeta',
    }),
    makeCurrentPresetOptions(): void {
      this.setCurrentPresetOptions(cloneDeep(this.currentPreset.presetOptions.spacings));
    },
    isValid(valid: boolean): void {
      this.isReadyToSave = valid;
    },
    deleteElement(index: number): void {
      if (!this.isReadyToSave) return;

      if (this.currentPresetOptions[index].id) {
        this.currentPresetOptions[index].destroy = true;
        this.currentPresetOptions[index].name = undefined;
      } else {
        this.currentPresetOptions.splice(index, 1);
      }
    },
    addNewElementAction(): void {
      if (!this.isReadyToSave) return;
      if (!this.newElementValid) return;

      const obSpacingAdd: PresetOptionSpacing = {
        id: `gpid_${HELPER.MakeId()}`,
        name: this.generateDuplicateName(),
        isDefault: false,
        destroy: false,
        type: 'spacing',
        value: {
          value: '0px',
        },
      };
      this.currentPresetOptions.push(obSpacingAdd);
    },
    generateDuplicateName(): string {
      const elementNames = SPACING_DEFAULT_NAMES.filter((item: any) => !this.elementNames.includes(item));
      return elementNames[0];
    },
    duplicateItem(index: number) {
      if (!this.isReadyToSave) return;
      if (!this.newElementValid) return;

      const iDuplicate = this.currentPresetOptions[index];
      const duplicateItem: PresetOptionSpacing = {
        id: `gpid_${HELPER.MakeId()}`,
        isDefault: false,
        destroy: false,
        name: this.generateDuplicateName(),
        type: 'spacing',
        value: JSON.parse(JSON.stringify(iDuplicate.value)),
      };

      this.currentPresetOptions.push(duplicateItem);
    },
    isDestroyed(element: any) {
      return !element.destroy;
    },
    setWidthToolTip(): void {
      this.tWidth = '150px';
      this.isOpenTooltip = true;
    },
    resetWidthToolTip(): void {
      this.tWidth = 'auto';
      this.isOpenTooltip = false;
    },
  } as any,
});
</script>

<style scoped lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
.gf_spacing {
  color: $neutral_6;
  height: calc(100% - 116px);
  &__scroll-body {
    height: 100%;
    padding: 12px;
    display: flex;
    flex-direction: column;

    .body {
      flex: 1;
    }
  }
  &_pack-name {
    font-size: 11px;
    font-weight: 500;
    margin-bottom: 17px;
    margin-top: 4px;

    &--value {
      margin-right: 5.5px;
    }

    &--notice {
      font-size: 14px;
    }
  }

  &_pack-elements {
    background: $neutral_2;
    border-radius: 4px;
    padding: 8px;
  }

  &_pack-actions {
    border-radius: 3px;
    border: 1px dashed $neutral_5;
    cursor: pointer;
    margin-top: 16px;
    padding: 9px 0px;

    &--label {
      font-size: 12px;
      line-height: 14px;
      color: $textDarkHigh;
    }

    &--icon {
      margin-top: -1px;
      width: 11px;
      margin-right: 10px;
    }
  }

  &_input {
    width: 80px;
  }
}
</style>
